import { useRef, useState } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import Header from './Header';
import Body from './Body';
import DialogAction from './DialogAction';
import {
  INDICATION_REQUEST_LIST_PRINT_TYPE,
  LOCAL_STORAGE_INDICATION_LIST_COMPACT_MODE,
  LOCAL_STORAGE_INDICATION_LIST_PRINT_TYPE,
} from './constant';
import useSWR from 'swr';
import { API_SERVER } from '../../../../common/api';
import { axiosThunk } from '../../../../common/redux/axios';
import useGeneralHook from '../../../../common/hook/useGeneralHook';
import LoadingIcon from '../../../../common/component/LoadingIcon';
import { styled } from '@mui/system';
import { IndicationLaboratoryListSchema, IndicationRequest } from 'modules/schema';
import { useFetch } from 'modules/common/hook';

interface Props {
  onClose: () => void;
  indicationList: IndicationRequest[];
}

const DividerStyled = styled(Divider)({
  marginTop: '30px',
  marginBottom: '15px',
  '@media print': {
    display: 'none',
  },
});

const PrintDialogContent = ({ onClose, indicationList }: Props) => {
  const printContentRef = useRef(null);
  const [printType, setPrintType] = useState<INDICATION_REQUEST_LIST_PRINT_TYPE>(
    (sessionStorage.getItem(LOCAL_STORAGE_INDICATION_LIST_PRINT_TYPE) as INDICATION_REQUEST_LIST_PRINT_TYPE) || 'GROUP',
  );
  const [compactMode, setCompactMode] = useState(
    Boolean(sessionStorage.getItem(LOCAL_STORAGE_INDICATION_LIST_COMPACT_MODE)) || false,
  );
  const { dispatch } = useGeneralHook();
  const { data: therapy, isValidating: loadingTherapy } = useFetch(
    API_SERVER.therapy.detail(indicationList[0]?.therapyId!),
  );

  const { data: indicationLabs, isValidating: loadingIndicationLabs } = useSWR(
    indicationList.map((ele) => ele.id),
    async (...indicationIds) => {
      const data = await Promise.all(
        indicationIds.map((id) => dispatch(axiosThunk({ url: API_SERVER.indicationLaboratory.getList(id).url }))),
      );
      return data?.map((ele) => IndicationLaboratoryListSchema.parse(ele?.data));
    },
  );

  if (loadingTherapy || !therapy || loadingIndicationLabs || !indicationLabs)
    return (
      <Stack height="850px" justifyContent="center" alignItems="center">
        <LoadingIcon />
      </Stack>
    );

  return (
    <Box paddingTop="̣9px" paddingBottom="40px">
      <Box ref={printContentRef}>
        {printType === 'GROUP' ? (
          <Box sx={{ pageBreakBefore: 'always' }}>
            <Header />
            <Body therapy={therapy} indicationLabs={indicationLabs} compact={compactMode} />
          </Box>
        ) : (
          indicationList.map((indication, index) => (
            <Box sx={{ pageBreakBefore: 'always' }}>
              <Header />
              <Body therapy={therapy} indicationLabs={[indicationLabs[index]]} compact={compactMode} />
              <DividerStyled />
            </Box>
          ))
        )}
      </Box>
      <DialogAction
        onClose={onClose}
        printContentRef={printContentRef}
        printType={printType}
        onPrintTypeChange={setPrintType}
        compactMode={compactMode}
        onCompactModeChange={setCompactMode}
      />
    </Box>
  );
};

export default PrintDialogContent;
